/**
 * Collection listing "Load More".
 *
 * Replaces the client-side slicing of app/components/shop/Shop.vue: the button
 * asks the Section Rendering API for the next paginated page of this same
 * section and appends its product cards to the grid.
 *
 * Progressive enhancement: the button is server-rendered with `hidden` and the
 * ordinary pagination links are visible, so a browser without JS (or with this
 * file blocked) still walks the collection page by page.
 *
 * Accessibility: appending cards moves nothing for a keyboard or screen-reader
 * user by itself, so after each append focus lands on the first new card and a
 * polite live region announces "Showing X of Y".
 */
(function () {
  'use strict';

  var LOADING_CLASS = 'is-loading';

  function sectionUrl(url, sectionId) {
    var separator = url.indexOf('?') === -1 ? '?' : '&';
    return url + separator + 'section_id=' + encodeURIComponent(sectionId);
  }

  function init(root) {
    if (!root || root.dataset.loadMoreReady === 'true') return;

    var grid = root.querySelector('[data-products-grid]');
    var wrap = root.querySelector('[data-load-more]');
    var button = root.querySelector('[data-load-more-button]');
    var pagination = root.querySelector('[data-pagination]');
    var status = root.querySelector('[data-load-more-status]');
    var sectionId = root.getAttribute('data-section-id');
    var totalCount = Number(root.getAttribute('data-total-count')) || 0;

    if (!grid || !wrap || !button || !sectionId) return;

    root.dataset.loadMoreReady = 'true';
    wrap.hidden = false;
    if (pagination) pagination.hidden = true;

    function fallbackToPagination() {
      wrap.hidden = true;
      if (pagination) pagination.hidden = false;
    }

    function done() {
      button.classList.remove(LOADING_CLASS);
      button.disabled = false;
    }

    function announce() {
      if (!status) return;
      // Cards only: the archive grid also carries Start a project banners
      // (sections/main-archive.liquid), which are not products.
      var shown = grid.querySelectorAll('.product-listing-item').length;
      status.textContent = totalCount
        ? 'Showing ' + shown + ' of ' + totalCount
        : 'Showing ' + shown;
    }

    // The first link (or any focusable) inside the first card that was just
    // appended, so Tab carries on from the new content rather than the button.
    function focusFirstNew(firstNew) {
      if (!firstNew) return;
      var target = firstNew.matches('a[href]') ? firstNew : firstNew.querySelector('a[href], button:not([disabled])');
      if (!target) {
        if (!firstNew.hasAttribute('tabindex')) firstNew.setAttribute('tabindex', '-1');
        target = firstNew;
      }
      try {
        target.focus({ preventScroll: true });
      } catch (error) {
        target.focus();
      }
    }

    button.addEventListener('click', function () {
      var nextUrl = button.getAttribute('data-next-url');
      if (!nextUrl || button.disabled) return;

      button.disabled = true;
      button.classList.add(LOADING_CLASS);

      fetch(sectionUrl(nextUrl, sectionId), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (response) {
          if (!response.ok) throw new Error('Section request failed');
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var nextGrid = doc.querySelector('[data-products-grid]');

          var firstNew = null;
          if (nextGrid) {
            while (nextGrid.firstElementChild) {
              var appended = grid.appendChild(nextGrid.firstElementChild);
              if (!firstNew) firstNew = appended;
            }
          }

          announce();
          focusFirstNew(firstNew);

          var nextButton = doc.querySelector('[data-load-more-button]');
          if (nextButton && nextButton.getAttribute('data-next-url')) {
            button.setAttribute('data-next-url', nextButton.getAttribute('data-next-url'));
            done();
          } else {
            wrap.hidden = true;
            done();
          }
        })
        .catch(function () {
          done();
          fallbackToPagination();
        });
    });
  }

  function initAll(scope) {
    var container = scope || document;
    var roots = container.querySelectorAll('[data-collection]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
